import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    createBandTrackPlaySession,
    deleteBandMediaPhoto,
    deleteBandTrack,
    getBandPlaysCount,
    listBandMediaPhotos,
    listBandTracks,
    recordBandTrackPlay,
    reorderBandMediaPhotos,
    reorderBandTracks,
    uploadBandMediaPhoto,
    uploadBandTrack,
} from "../../../../services/mediaService";
import useProfileTrackPlayback from "../../../../hooks/useProfileTrackPlayback";
import { PROFILE_TRACK_MAX_SIZE_MB } from "../../../../constants/media";
import {
    applyTrackPlaysCountByIdentity,
    getMediaPhotoIdentity,
    getTrackIdentity,
    moveTrackByIdentity,
    reorderMediaPhotosByIdentity,
    stripFileExtension,
} from "../../../../utils/profileEditorial";
import { normalizePlaysCount } from "../../../../utils/playsCount";
import {
    isBandGalleryPhotoForBand,
    mergeBandGalleryPhotos,
    mergeBandTracks,
    replaceBandGalleryPhotos,
} from "../utils/bandWelcomeTransforms";

const getBandPlaybackImage = (band) => (
    band?.images?.avatar?.url
    || band?.avatarUrl
    || band?.imageUrl
    || band?.profileImageUrl
    || null
);

export default function useBandMediaLibrary({
    selectedBand,
    currentUserId,
    canEditSelectedBand,
    audience = "viewer",
    previewRequestIdRef,
}) {
    const { t } = useTranslation();
    const bandGalleryRequestIdRef = useRef(0);
    const bandGalleryUploadRequestIdRef = useRef(0);
    const bandTrackRequestIdRef = useRef(0);
    const bandPlaysCountRequestIdRef = useRef(0);
    const bandTrackUploadRequestIdRef = useRef(0);
    const bandGalleryPhotoInputRef = useRef(null);
    const bandTrackInputRef = useRef(null);

    const [bandGalleryPhotos, setBandGalleryPhotos] = useState([]);
    const [isBandGalleryPhotoUploading, setIsBandGalleryPhotoUploading] = useState(false);
    const [isBandGalleryPhotoReordering, setIsBandGalleryPhotoReordering] = useState(false);
    const [deletingBandGalleryPhotoId, setDeletingBandGalleryPhotoId] = useState(null);
    const [bandTracks, setBandTracks] = useState([]);
    const [isBandTrackUploading, setIsBandTrackUploading] = useState(false);
    const [isBandTrackReordering, setIsBandTrackReordering] = useState(false);
    const [deletingBandTrackId, setDeletingBandTrackId] = useState(null);
    const [bandPlaysCount, setBandPlaysCount] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = "info") => {
        setToast({
            id: Date.now(),
            message,
            type,
        });
    }, []);

    const clearToast = useCallback(() => {
        setToast(null);
    }, []);

    const handleBandTrackPlaybackError = useCallback((message) => {
        showToast(message, "error");
    }, [showToast]);

    const clearBandGalleryState = useCallback(() => {
        setBandGalleryPhotos([]);
        setIsBandGalleryPhotoUploading(false);
        setIsBandGalleryPhotoReordering(false);
        setDeletingBandGalleryPhotoId(null);
    }, []);

    const clearBandTrackState = useCallback(() => {
        setBandTracks([]);
        setBandPlaysCount(null);
        setIsBandTrackUploading(false);
        setIsBandTrackReordering(false);
        setDeletingBandTrackId(null);
    }, []);

    const resetBandMediaState = useCallback(() => {
        bandGalleryRequestIdRef.current += 1;
        bandGalleryUploadRequestIdRef.current += 1;
        bandTrackRequestIdRef.current += 1;
        bandPlaysCountRequestIdRef.current += 1;
        bandTrackUploadRequestIdRef.current += 1;
        clearBandGalleryState();
        clearBandTrackState();
    }, [clearBandGalleryState, clearBandTrackState]);

    const loadBandPlaysCount = useCallback((bandId) => (
        audience === "public"
            ? getBandPlaysCount(bandId, { audience: "public" })
            : getBandPlaysCount(bandId)
    ), [audience]);

    const refreshBandPlaysCount = useCallback((bandId) => {
        const requestId = bandPlaysCountRequestIdRef.current + 1;
        bandPlaysCountRequestIdRef.current = requestId;
        return loadBandPlaysCount(bandId)
            .then((response) => {
                if (bandPlaysCountRequestIdRef.current !== requestId) return;
                setBandPlaysCount(normalizePlaysCount(response?.playsCount));
            })
            .catch((err) => {
                if (bandPlaysCountRequestIdRef.current !== requestId) return;
                console.error("Failed to load band plays count:", err);
            });
    }, [loadBandPlaysCount]);

    const applyBandTrackPlaysCount = useCallback((nextCount, track, response) => {
        setBandTracks((current) => (
            applyTrackPlaysCountByIdentity(current, track, nextCount)
        ));
        const nextBandPlaysCount = normalizePlaysCount(response?.bandPlaysCount);
        if (nextBandPlaysCount !== null) {
            bandPlaysCountRequestIdRef.current += 1;
            setBandPlaysCount(nextBandPlaysCount);
        }
    }, []);

    const {
        playingTrackId,
        trackProgressById,
        handlePlayTrack,
        handleSeekTrack,
        pauseCurrentTrack,
    } = useProfileTrackPlayback({
        subjectId: selectedBand?.id,
        audience,
        sourceType: "band",
        tracks: bandTracks,
        createTrackPlaySession: createBandTrackPlaySession,
        recordTrackPlay: recordBandTrackPlay,
        onPlaysCountChange: applyBandTrackPlaysCount,
        onError: handleBandTrackPlaybackError,
        errorMessage: t("bandsWelcomePage.media.trackPlayFailed", { defaultValue: "Unable to play this track." }),
        logLabel: "band track",
        sourceLabel: selectedBand?.name || "",
        sourceSubtitle: t("bandPage.media.tracksMeta", { defaultValue: "Demos and live takes" }),
        sourceImage: getBandPlaybackImage(selectedBand),
    });

    useEffect(() => {
        const bandId = selectedBand?.id;
        const requestId = bandGalleryRequestIdRef.current + 1;
        bandGalleryRequestIdRef.current = requestId;
        if (!bandId || (!currentUserId && audience !== "public")) {
            clearBandGalleryState();
            return undefined;
        }

        clearBandGalleryState();
        const loadPhotos = audience === "public"
            ? listBandMediaPhotos(bandId, { audience: "public" })
            : listBandMediaPhotos(bandId);
        loadPhotos
            .then((photos) => {
                if (bandGalleryRequestIdRef.current !== requestId) return;
                const loadedPhotos = Array.isArray(photos) ? photos : [];
                setBandGalleryPhotos((current) => mergeBandGalleryPhotos(current, loadedPhotos));
            })
            .catch((err) => {
                if (bandGalleryRequestIdRef.current !== requestId) return;
                console.error("Failed to load band gallery photos:", err);
            });

        return () => {
            bandGalleryRequestIdRef.current += 1;
            bandGalleryUploadRequestIdRef.current += 1;
        };
    }, [audience, clearBandGalleryState, currentUserId, selectedBand?.id]);

    useEffect(() => {
        const bandId = selectedBand?.id;
        const requestId = bandTrackRequestIdRef.current + 1;
        bandTrackRequestIdRef.current = requestId;
        if (!bandId || (!currentUserId && audience !== "public")) {
            clearBandTrackState();
            return undefined;
        }

        clearBandTrackState();
        const loadTracks = audience === "public"
            ? listBandTracks(bandId, { audience: "public" })
            : listBandTracks(bandId);
        loadTracks
            .then((tracks) => {
                if (bandTrackRequestIdRef.current !== requestId) return;
                const loadedTracks = Array.isArray(tracks) ? tracks : [];
                setBandTracks((current) => mergeBandTracks(current, loadedTracks));
            })
            .catch((err) => {
                if (bandTrackRequestIdRef.current !== requestId) return;
                console.error("Failed to load band tracks:", err);
            });
        void refreshBandPlaysCount(bandId);

        return () => {
            bandTrackRequestIdRef.current += 1;
            bandPlaysCountRequestIdRef.current += 1;
            bandTrackUploadRequestIdRef.current += 1;
        };
    }, [audience, clearBandTrackState, currentUserId, refreshBandPlaysCount, selectedBand?.id]);

    const handleOpenBandGalleryPhotoUpload = useCallback(() => {
        if (!canEditSelectedBand || isBandGalleryPhotoUploading) {
            return;
        }
        bandGalleryPhotoInputRef.current?.click();
    }, [canEditSelectedBand, isBandGalleryPhotoUploading]);

    const handleBandGalleryPhotoFileChange = useCallback(async (event) => {
        const files = Array.from(event?.target?.files || []);
        if (event?.target) {
            event.target.value = "";
        }
        const bandId = selectedBand?.id;
        if (!canEditSelectedBand || !bandId || files.length === 0) {
            return;
        }

        const uploadRequestId = bandGalleryUploadRequestIdRef.current + 1;
        bandGalleryUploadRequestIdRef.current = uploadRequestId;
        setIsBandGalleryPhotoUploading(true);
        try {
            for (const file of files) {
                const uploadedPhoto = await uploadBandMediaPhoto(bandId, file);
                if (bandGalleryUploadRequestIdRef.current !== uploadRequestId) {
                    return;
                }
                setBandGalleryPhotos((current) => mergeBandGalleryPhotos([uploadedPhoto], current));
            }
            if (bandGalleryUploadRequestIdRef.current !== uploadRequestId) {
                return;
            }
            showToast(
                t("bandsWelcomePage.gallery.photoUploaded", { defaultValue: "Photo uploaded." }),
                "success",
            );
        } catch (err) {
            if (bandGalleryUploadRequestIdRef.current !== uploadRequestId) {
                return;
            }
            console.error("Failed to upload band gallery photo:", err);
            showToast(
                err?.message || t("bandsWelcomePage.gallery.photoUploadFailed", { defaultValue: "Photo upload failed." }),
                "error",
            );
        } finally {
            if (bandGalleryUploadRequestIdRef.current === uploadRequestId) {
                setIsBandGalleryPhotoUploading(false);
            }
        }
    }, [canEditSelectedBand, selectedBand?.id, showToast, t]);

    const handleDeleteBandGalleryPhoto = useCallback(async (photo) => {
        const bandId = selectedBand?.id;
        const photoId = getMediaPhotoIdentity(photo);
        if (
            !canEditSelectedBand
            || !bandId
            || !photoId
            || !photo?.objectKey
            || deletingBandGalleryPhotoId
            || isBandGalleryPhotoReordering
        ) {
            return;
        }

        const requestId = previewRequestIdRef.current;
        setDeletingBandGalleryPhotoId(photoId);
        try {
            await deleteBandMediaPhoto(bandId, photo.objectKey);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setBandGalleryPhotos((current) => current.filter((item) => getMediaPhotoIdentity(item) !== photoId));
            showToast(
                t("bandsWelcomePage.gallery.photoDeleted", { defaultValue: "Photo deleted." }),
                "success",
            );
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            console.error("Failed to delete band gallery photo:", err);
            showToast(
                err?.response?.data?.message
                    || err?.message
                    || t("bandsWelcomePage.gallery.photoDeleteFailed", { defaultValue: "Photo delete failed." }),
                "error",
            );
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setDeletingBandGalleryPhotoId(null);
            }
        }
    }, [
        canEditSelectedBand,
        deletingBandGalleryPhotoId,
        isBandGalleryPhotoReordering,
        previewRequestIdRef,
        selectedBand?.id,
        showToast,
        t,
    ]);

    const handleReorderBandGalleryPhotos = useCallback(async (nextPhotos) => {
        const bandId = selectedBand?.id;
        if (
            !canEditSelectedBand
            || !bandId
            || isBandGalleryPhotoReordering
            || deletingBandGalleryPhotoId
        ) {
            return;
        }

        const orderedPhotoIds = (Array.isArray(nextPhotos) ? nextPhotos : [])
            .map(getMediaPhotoIdentity)
            .filter(Boolean);
        const currentBandPhotos = (Array.isArray(bandGalleryPhotos) ? bandGalleryPhotos : [])
            .filter((photo) => isBandGalleryPhotoForBand(photo, bandId));
        const reorderedPhotos = reorderMediaPhotosByIdentity(currentBandPhotos, orderedPhotoIds);
        if (reorderedPhotos === currentBandPhotos || reorderedPhotos.length === 0) {
            return;
        }

        const objectKeys = reorderedPhotos.map((item) => item?.objectKey).filter(Boolean);
        if (objectKeys.length !== reorderedPhotos.length) {
            showToast(
                t("bandsWelcomePage.gallery.photoReorderFailed", { defaultValue: "Photo reorder failed." }),
                "error",
            );
            return;
        }

        const requestId = previewRequestIdRef.current;
        setIsBandGalleryPhotoReordering(true);
        setBandGalleryPhotos((current) => replaceBandGalleryPhotos(current, bandId, reorderedPhotos));
        try {
            const persistedPhotos = await reorderBandMediaPhotos(bandId, objectKeys);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            if (Array.isArray(persistedPhotos)) {
                setBandGalleryPhotos((current) => replaceBandGalleryPhotos(current, bandId, persistedPhotos));
            }
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            console.error("Failed to reorder band gallery photos:", err);
            setBandGalleryPhotos((current) => replaceBandGalleryPhotos(current, bandId, currentBandPhotos));
            showToast(
                err?.response?.data?.message
                    || err?.message
                    || t("bandsWelcomePage.gallery.photoReorderFailed", { defaultValue: "Photo reorder failed." }),
                "error",
            );
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsBandGalleryPhotoReordering(false);
            }
        }
    }, [
        bandGalleryPhotos,
        canEditSelectedBand,
        deletingBandGalleryPhotoId,
        isBandGalleryPhotoReordering,
        previewRequestIdRef,
        selectedBand?.id,
        showToast,
        t,
    ]);

    const handleOpenBandTrackUpload = useCallback(() => {
        if (!canEditSelectedBand || isBandTrackUploading) {
            return;
        }
        bandTrackInputRef.current?.click();
    }, [canEditSelectedBand, isBandTrackUploading]);

    const handleBandTrackFileChange = useCallback(async (event) => {
        const file = event?.target?.files?.[0] || null;
        if (event?.target) {
            event.target.value = "";
        }
        const bandId = selectedBand?.id;
        if (!canEditSelectedBand || !bandId || !file || isBandTrackUploading) {
            return;
        }

        if (file.type && !file.type.toLowerCase().startsWith("audio/")) {
            showToast(
                t("bandsWelcomePage.media.trackInvalidType", { defaultValue: "Choose an audio file." }),
                "error",
            );
            return;
        }

        const maxBytes = Math.max(1, PROFILE_TRACK_MAX_SIZE_MB) * 1024 * 1024;
        if (file.size > maxBytes) {
            showToast(
                t("bandsWelcomePage.media.trackTooLarge", {
                    max: PROFILE_TRACK_MAX_SIZE_MB,
                    defaultValue: "Audio files must be {{max}} MB or smaller.",
                }),
                "error",
            );
            return;
        }

        const uploadRequestId = bandTrackUploadRequestIdRef.current + 1;
        bandTrackUploadRequestIdRef.current = uploadRequestId;
        setIsBandTrackUploading(true);
        try {
            const uploadedTrack = await uploadBandTrack(bandId, file, {
                title: stripFileExtension(file.name),
            });
            if (bandTrackUploadRequestIdRef.current !== uploadRequestId) {
                return;
            }
            const uploadedIdentity = getTrackIdentity(uploadedTrack);
            setBandTracks((current) => [
                uploadedTrack,
                ...(uploadedIdentity
                    ? current.filter((track) => getTrackIdentity(track) !== uploadedIdentity)
                    : current),
            ]);
            showToast(
                t("bandsWelcomePage.media.trackUploaded", { defaultValue: "Track uploaded." }),
                "success",
            );
        } catch (err) {
            if (bandTrackUploadRequestIdRef.current !== uploadRequestId) {
                return;
            }
            console.error("Failed to upload band track:", err);
            showToast(
                err?.response?.data?.message
                    || err?.message
                    || t("bandsWelcomePage.media.trackUploadFailed", { defaultValue: "Track upload failed." }),
                "error",
            );
        } finally {
            if (bandTrackUploadRequestIdRef.current === uploadRequestId) {
                setIsBandTrackUploading(false);
            }
        }
    }, [canEditSelectedBand, isBandTrackUploading, selectedBand?.id, showToast, t]);

    const handleDeleteBandTrack = useCallback(async (track) => {
        const bandId = selectedBand?.id;
        const trackId = getTrackIdentity(track);
        if (
            !canEditSelectedBand
            || !bandId
            || !trackId
            || !track?.objectKey
            || deletingBandTrackId
            || isBandTrackReordering
        ) {
            return;
        }

        const requestId = previewRequestIdRef.current;
        setDeletingBandTrackId(trackId);
        try {
            if (playingTrackId === trackId) {
                pauseCurrentTrack(track);
            }
            await deleteBandTrack(bandId, track.objectKey);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            setBandTracks((current) => current.filter((item) => getTrackIdentity(item) !== trackId));
            const deletedTrackPlaysCount = normalizePlaysCount(track?.playsCount);
            if (deletedTrackPlaysCount !== null) {
                setBandPlaysCount((current) => {
                    const currentCount = normalizePlaysCount(current);
                    return currentCount === null ? current : Math.max(0, currentCount - deletedTrackPlaysCount);
                });
            }
            void refreshBandPlaysCount(bandId);
            showToast(
                t("bandsWelcomePage.media.trackDeleted", { defaultValue: "Track deleted." }),
                "success",
            );
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            console.error("Failed to delete band track:", err);
            showToast(
                err?.response?.data?.message
                    || err?.message
                    || t("bandsWelcomePage.media.trackDeleteFailed", { defaultValue: "Track delete failed." }),
                "error",
            );
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setDeletingBandTrackId(null);
            }
        }
    }, [
        canEditSelectedBand,
        deletingBandTrackId,
        isBandTrackReordering,
        pauseCurrentTrack,
        playingTrackId,
        previewRequestIdRef,
        refreshBandPlaysCount,
        selectedBand?.id,
        showToast,
        t,
    ]);

    const handleMoveBandTrack = useCallback(async (track, direction) => {
        const bandId = selectedBand?.id;
        const trackId = getTrackIdentity(track);
        if (
            !canEditSelectedBand
            || !bandId
            || !trackId
            || deletingBandTrackId
            || isBandTrackReordering
        ) {
            return;
        }

        const currentTracks = Array.isArray(bandTracks) ? bandTracks : [];
        const nextTracks = moveTrackByIdentity(currentTracks, trackId, direction);
        if (nextTracks === currentTracks || nextTracks.length === 0) {
            return;
        }
        const objectKeys = nextTracks.map((item) => item?.objectKey).filter(Boolean);
        if (objectKeys.length !== nextTracks.length) {
            showToast(
                t("bandsWelcomePage.media.trackReorderFailed", { defaultValue: "Track reorder failed." }),
                "error",
            );
            return;
        }

        const requestId = previewRequestIdRef.current;
        setIsBandTrackReordering(true);
        setBandTracks(nextTracks);
        try {
            const persistedTracks = await reorderBandTracks(bandId, objectKeys);
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            if (Array.isArray(persistedTracks)) {
                setBandTracks(persistedTracks);
            }
        } catch (err) {
            if (previewRequestIdRef.current !== requestId) {
                return;
            }
            console.error("Failed to reorder band tracks:", err);
            setBandTracks(currentTracks);
            showToast(
                err?.response?.data?.message
                    || err?.message
                    || t("bandsWelcomePage.media.trackReorderFailed", { defaultValue: "Track reorder failed." }),
                "error",
            );
        } finally {
            if (previewRequestIdRef.current === requestId) {
                setIsBandTrackReordering(false);
            }
        }
    }, [
        bandTracks,
        canEditSelectedBand,
        deletingBandTrackId,
        isBandTrackReordering,
        previewRequestIdRef,
        selectedBand?.id,
        showToast,
        t,
    ]);

    const selectedBandGalleryPhotos = useMemo(() => {
        const bandId = selectedBand?.id;
        if (!bandId) {
            return [];
        }
        return (Array.isArray(bandGalleryPhotos) ? bandGalleryPhotos : [])
            .filter((photo) => isBandGalleryPhotoForBand(photo, bandId));
    }, [bandGalleryPhotos, selectedBand?.id]);

    return {
        bandGalleryPhotoInputRef,
        bandTrackInputRef,
        resetBandMediaState,
        toast,
        clearToast,
        galleryPhotos: selectedBandGalleryPhotos,
        tracks: bandTracks,
        playsCount: bandPlaysCount,
        playingTrackId,
        trackProgressById,
        deletingGalleryPhotoId: deletingBandGalleryPhotoId,
        deletingTrackId: deletingBandTrackId,
        isGalleryPhotoReordering: isBandGalleryPhotoReordering,
        isTrackReordering: isBandTrackReordering,
        handleOpenBandGalleryPhotoUpload,
        handleBandGalleryPhotoFileChange,
        handleDeleteBandGalleryPhoto,
        handleReorderBandGalleryPhotos,
        handleOpenBandTrackUpload,
        handleBandTrackFileChange,
        handleDeleteBandTrack,
        handleMoveBandTrack,
        handlePlayTrack,
        handleSeekTrack,
    };
}
